import type {
  EditorAPI,
  IComponentPanelProps,
} from '@wix/editor-elements-types/editor';
import { isEmptyObject, isThemeFont } from '@wix/editor-elements-common-utils';
import { clamp, round } from '@wix/wow-utils';
import {
  MIN_FONT,
  MAX_FONT,
  COLOR_PICKER_X_OFFSET,
  COLOR_PICKER_Y_OFFSET,
} from '../../../constants';
import type { ColorOrGradient } from '@wix/background-media-utils';
import type { TextEffectsSharedSkin } from './TextEffectsShared.designPanel.types';
import type { FontTheme } from '@wix/editor-elements-panel';
import type { StyleItem } from '@wix/editor-elements-types/thunderbolt';
import type { Shadow } from './components/DesignPanel.types';

const OFFSET_MULTIPLIER = 100;
const BLUR_MULTIPLIER = 500;

export const getFontSizeToDisplay = ({
  fontSize = '',
  normalizedFontSizeMap,
  minFontSize,
  maxFontSize,
}: {
  fontSize?: string;
  normalizedFontSizeMap?: Record<string, string>;
  minFontSize?: string;
  maxFontSize?: string;
}) => {
  const value = getFontValue(fontSize, normalizedFontSizeMap);
  const minFontSizeVal = minFontSize && parseInt(minFontSize, 10);
  const maxFontSizeVal = maxFontSize && parseInt(maxFontSize, 10);
  if (getFontSizeUnit(fontSize, normalizedFontSizeMap) === 'spx') {
    if (minFontSizeVal && minFontSizeVal > value) {
      return minFontSizeVal;
    } else if (maxFontSizeVal && maxFontSizeVal < value) {
      return maxFontSizeVal;
    }
  }
  return value;
};

const getFontSizeWithMap = (
  size: string,
  normalizedFontSizeMap?: Record<string, string>,
) =>
  isThemeFont(size) && normalizedFontSizeMap
    ? normalizedFontSizeMap[size]
    : size;

export const getFontSizeUnit = (
  fontsize: string = '',
  normalizedFontSizeMap?: Record<string, string>,
) => {
  const fontSize = getFontSizeWithMap(fontsize, normalizedFontSizeMap);
  return fontSize.endsWith('spx') ? 'spx' : 'px';
};

export const getFontSizeUnitDisplay = (
  fontsize: string = '',
  normalizedFontSizeMap?: Record<string, string>,
) =>
  getFontSizeUnit(fontsize, normalizedFontSizeMap) === 'spx' ? 'px*' : 'px';

export const getNormalizedFontsSize = (fontThemes: EditorAPI['fontThemes']) => {
  const fontSizeObject: Record<string, string> = {};
  Object.keys(fontThemes).forEach(key => {
    const font = fontThemes[key];
    fontSizeObject[key] = font.size;
  });
  return fontSizeObject;
};

export const getFontValue = (
  fontSize: string,
  normalizedFontSizeMap?: Record<string, string>,
) => {
  const size = getFontSizeWithMap(fontSize, normalizedFontSizeMap);
  return parseInt(size, 10);
};

export const fontSizeValidation = (value: number) => {
  const validatedValue = clamp(MIN_FONT, MAX_FONT, round(1, value));
  return validatedValue;
};

export function openColorPicker(
  color: ColorOrGradient,
  onChange: (newValue: ColorOrGradient) => void,
  rect: DOMRect,
  openColorPickerProps: (
    color: ColorOrGradient,
    position: { top: number; left: number },
    options: {
      onChange: (value: ColorOrGradient | null) => void;
      onCancel: (value: ColorOrGradient | null) => void;
      panelMode: string;
      overridePosition: boolean;
      showColorModalConfirmButtons: boolean;
    },
  ) => void,
  panelMode = 'solid',
) {
  const position = {
    top: rect.top + COLOR_PICKER_Y_OFFSET,
    left: rect.left + COLOR_PICKER_X_OFFSET,
  };

  const _onChange = (value: ColorOrGradient | null) => {
    if (value !== null) {
      onChange(value);
    }
  };
  const _onCancel = _onChange;

  openColorPickerProps(color, position, {
    onChange: _onChange,
    onCancel: _onCancel,
    panelMode,
    overridePosition: false,
    showColorModalConfirmButtons: false,
  });
}

export const cleanUnnecessaryFontOverrides = (
  fontOverrides: NonNullable<
    StyleItem<TextEffectsSharedSkin>['style']['propertiesOverride']['font']
  >,
  fontTheme: FontTheme,
) => {
  const overrides: typeof fontOverrides = {};
  const { fontFamily, fontSize, fontStyle, fontWeight } = fontOverrides;

  const {
    family: themeFamily,
    size: themeSize,
    italic: themeIsItalic,
    weight: themeWeight,
  } = fontTheme;

  if (fontFamily !== undefined && fontFamily !== themeFamily) {
    overrides.fontFamily = fontFamily;
  }

  if (fontSize !== undefined && fontSize !== themeSize) {
    overrides.fontSize = fontSize;
  }

  if (fontStyle !== undefined) {
    const themeEffectiveFontStyle = themeIsItalic ? 'italic' : 'normal';
    if (fontStyle !== themeEffectiveFontStyle) {
      overrides.fontStyle = fontStyle;
    }
  }

  if (
    fontWeight !== undefined &&
    getFontWeightAsNumber(fontWeight) !== getFontWeightAsNumber(themeWeight)
  ) {
    overrides.fontWeight = fontWeight;
  }

  return isEmptyObject(overrides) ? undefined : overrides;
};

export const getUpdatedFontProperties = <
  T extends IComponentPanelProps<never, any>,
>(
  compStyle: T['compStyle'],
  fontThemes: EditorAPI['fontThemes'],
  newFontProperties: Partial<T['compStyle']['style']['properties']>,
  newFontPropertiesOverride?:
    | 'clearOverride'
    | Partial<T['compStyle']['style']['propertiesOverride']['font']>,
) => {
  const currentFontProperties = compStyle.style.properties;
  const currentFontPropertiesOverride =
    compStyle.style.propertiesOverride?.font;

  const newProperties = { ...currentFontProperties, ...newFontProperties };

  const getFontPropertiesOverride = () => {
    if (newFontPropertiesOverride === 'clearOverride') {
      return undefined;
    }

    const fontPropertiesOverrides = {
      ...currentFontPropertiesOverride,
      ...newFontPropertiesOverride,
    };

    const cleanedFontOverride = cleanUnnecessaryFontOverrides(
      fontPropertiesOverrides,
      fontThemes[newProperties.font],
    );

    return cleanedFontOverride === undefined
      ? undefined
      : {
          font: cleanedFontOverride,
        };
  };

  return {
    ...compStyle,
    style: {
      ...compStyle.style,
      properties: newProperties,
      propertiesOverride: getFontPropertiesOverride(),
    },
  };
};

type Interval = readonly [number, number];

/*
 * A linear transform from interval [a, b] to interval [c, d] means receiving a
 * value x from interval [a, b] and returning its corresponding y from [c, d].
 *
 * The length of interval [a, b] is: b - a
 * The length of interval [c, d] is: d - c
 * The proportion is: (d - c) / (b - a)
 *
 * Given a value x from [a, b] we need to multiply it by this proportion to get
 * its corresponding value y in [c, d].
 * But before we do that, we need to account for the starting point of each
 * interval, a and c. So we subtract a before multiplying with the proportion,
 * and we add c after it.
 *
 * In summary:
 * Given x from [a, b] we subtract a, then multiply by the proportion, then we
 * add c, and we receive y.
 * y = (x - a) * ((d - c) / (b - a)) + c
 *
 * Example:
 * The psychometric test results are in the interval [200, 800]
 * Regular test results are in the interval [0, 100]
 *
 * If we want to convert a psychometric test result x into the regular interval
 * of [0, 100] we can do this:
 * y = (x - 200) * ((100 - 0) / (800 - 200)) + 0
 *   = (x - 200) / 6
 *
 * For example score 560 corresponds to (560 - 200) / 6 = 360 / 6 = 60
 *
 * If we want to convert the other way around from [0, 100] to [200, 800]:
 * y = (x - 0) * ((800 - 200) / (100 - 0)) + 200
 *   = x * 6 + 200
 *
 * For example score 60 corresponds to 60 * 6 + 200 = 360 + 200 = 560
 */
export const createLinearRangeTransformer =
  ([srcStart, srcEnd]: Interval, [dstStart, dstEnd]: Interval) =>
  (x: number) =>
    (x - srcStart) * ((dstEnd - dstStart) / (srcEnd - srcStart)) + dstStart;

/*
 * Create two-way transformers between two intervals
 */
export const createDualLinearRangeTransformer = (
  src: Interval,
  dst: Interval,
) => [
  createLinearRangeTransformer(src, dst),
  createLinearRangeTransformer(dst, src),
];

export const range = (start: number, end: number, step: number = 1) => {
  if ((start < end && step <= 0) || (end < start && step >= 0)) {
    throw new Error('Infinite loop detected');
  }

  const length = 1 + Math.floor((end - start) / step);
  return Array.from({ length }, (_, i) => start + i * step);
};

export const generateSliderMarks = (
  start: number,
  end: number,
  step: number = 1,
) => {
  return range(start, end, step).reduce<Record<number, string>>(
    (result, value) => {
      result[value] = value === start || value === end ? `${value}°` : '';
      return result;
    },
    {},
  );
};

export const getFontDisplayString = (fontFamily: string | undefined) =>
  fontFamily && fontFamily.replace(/\+/g, ' ');

export const convertShadowToString = (shadow: Shadow): string => {
  let colorValue = '';

  if (shadow.color.propertyType === 'ThemeColor') {
    colorValue = `rgba(var(--${shadow.color.theme}),${shadow.color.alpha.value})`;
  } else {
    colorValue = shadow.color.value;
  }

  // if offset and blur are in px, convert them to em
  const offsetXValue = shadow.offsetX.value / OFFSET_MULTIPLIER;
  const offsetYValue = shadow.offsetY.value / OFFSET_MULTIPLIER;
  const blurRadiusValue = shadow.blurRadius.value / BLUR_MULTIPLIER;

  return `${offsetXValue}em ${offsetYValue}em ${blurRadiusValue}em ${colorValue}`;
};

export const convertStringToShadow = (shadow: string): Shadow => {
  const [offsetX, offsetY, blurRadius, color] = shadow.split(' ');

  const themeColorRegex = /rgba\(var\(--([a-zA-Z0-9_]+)\),([0-9.]+)\)/;
  const match = color.match(themeColorRegex);

  const offsetXValue = parseFloat(offsetX) * OFFSET_MULTIPLIER;
  const offsetYValue = parseFloat(offsetY) * OFFSET_MULTIPLIER;
  const blurRadiusValue = parseFloat(blurRadius) * BLUR_MULTIPLIER;
  const colorValue: Shadow['color'] = match
    ? {
        propertyType: 'ThemeColor',
        theme: match[1],
        alpha: {
          propertyType: 'Alpha',
          value: parseFloat(match[2]),
        },
      }
    : {
        propertyType: 'Color',
        value: color,
      };

  return {
    offsetX: {
      propertyType: 'Size',
      value: offsetXValue,
    },
    offsetY: {
      propertyType: 'Size',
      value: offsetYValue,
    },
    blurRadius: {
      propertyType: 'Size',
      value: blurRadiusValue,
    },
    color: colorValue,
  };
};

export const getFontWeightAsNumber = (weight: string) => {
  if (weight === 'bold') {
    return 700;
  }

  if (weight === 'normal') {
    return 400;
  }

  const numericWeight = parseInt(weight, 10);
  return isNaN(numericWeight) ? 400 : numericWeight;
};

export const isBoldWeight = (fontWeight: string) => {
  return fontWeight === 'bold' || parseInt(fontWeight, 10) >= 700;
};

export const isBold = (
  font: string,
  fontWeightOverride: string | undefined,
  fontThemes: EditorAPI['fontThemes'],
) => {
  if (fontWeightOverride) {
    if (fontWeightOverride === 'normal') {
      return false;
    }
    return isBoldWeight(fontWeightOverride);
  }

  return isBoldWeight(fontThemes[font].weight);
};

export const isItalic = (
  font: string,
  fontStyleOverride: string | undefined,
  fontThemes: EditorAPI['fontThemes'],
) => {
  if (fontStyleOverride) {
    if (fontStyleOverride === 'normal') {
      return false;
    }
    return fontStyleOverride === 'italic';
  }

  return fontThemes[font].style === 'italic';
};

/**
 * Normalizes font weight values to ensure consistent handling across text components
 * when changing font families in the new font picker.
 *
 * @param newWeight - The new font weight from the font picker (number or undefined)
 * @param currentWeight - The current font weight value (number)
 * @returns Normalized font weight value or undefined if no change needed
 */
export const normalizeFontWeight = (
  newWeight: number | undefined,
  currentWeight: number,
): string | undefined => {
  if (newWeight === undefined) {
    return undefined;
  }

  // For TextEffects, we preserve the numeric representation
  // but still normalize the input to ensure consistency
  const normalizedWeight = newWeight.toString();

  // Only return the weight if it's different from current
  if (normalizedWeight !== currentWeight.toString()) {
    return normalizedWeight;
  }

  return undefined;
};
